// ========================== utils/shotstackBridge.js (full) ==========================
/* ------------------------------------------------------------------
   Shotstack bridge – every request goes THROUGH the backend proxy
   (/api/shotstack/render) so planLimits('video') can enforce credits.
------------------------------------------------------------------ */

const RENDER = '/api/shotstack/render';      // POST to start, GET /:id to poll

/* ================================================================
   startShotstackJob  → returns render-job ID (non-blocking)
================================================================ */
export async function startShotstackJob ({ timeline, output, callback }) {
  const body = callback ? { timeline, output, callback } : { timeline, output };

  const res = await fetch(RENDER, {
    method      : 'POST',
    headers     : { 'Content-Type': 'application/json' },
    body        : JSON.stringify(body),
    credentials : 'include',                // keep the session cookie
  });

  /* ---------- success ---------- */
  if (res.ok) {
    const json = await res.json();
    const id   = json?.response?.id ?? json?.id;
    if (!id) throw new Error('Shotstack did not return an ID');
    return id;
  }

  /* ---------- failure ---------- */
  let payload = {};
  try { payload = await res.json(); } catch {/* non-JSON response (HTML) */}
  const err = new Error(`Shotstack proxy POST failed (${res.status})`);
  err.status         = res.status;        // 403, 500, etc.
  err.response       = { data: payload }; // same shape axios would give
  err.userMsg        = payload.msg  || payload.error;
  err.serverErrorKey = payload.error;     // e.g. "video_limit_reached"
  throw err;
}

/* ================================================================
   pollShotstackJob  → waits until the render finishes
================================================================ */
export async function pollShotstackJob (id, onProgress = () => {}) {
  let url;
  while (!url) {
    await new Promise(r => setTimeout(r, 1500));

    const res = await fetch(`${RENDER}/${id}`, { credentials:'include' });
    if (!res.ok) throw new Error(`Shotstack proxy GET failed (${res.status})`);
    const poll = await res.json();

    const prog   = poll.response?.renderProgress ?? poll.renderProgress ?? 0;
    const status = poll.response?.status         ?? poll.status;
    onProgress(Math.round(prog * 100));

    if (status === 'failed')
      throw new Error(poll.response?.error || poll.error || 'Shotstack render failed');
    if (status === 'done')
      url = poll.response?.url ?? poll.url;
  }
  onProgress(100);
  return url;
}

/* ================================================================
   buildTimeline – exported so the UI can live-preview while rendering
================================================================ */
export function buildTimeline ({
  src, crop, preset, start, length,
  filter, textItems, audio, videoVolume,
}) {
  const dims = {
    '1:1'    : [1080, 1080],
    '4:5'    : [1080, 1350],
    '1.91:1' : [1920, 1080],
    '2:3'    : [1080, 1620],
    '9:16'   : [1080, 1920],
    keep     : [1920, 1080],
  };
  const [w, h] = dims[preset] || [1920, 1080];

  /* ---------- video clip ---------- */
  const vAsset = { type:'video', src, trim:start, volume: videoVolume };
  if (crop) {
    const pct = v => +(v / 100).toFixed(4);
    vAsset.crop = {
      top   : pct(crop.top),
      left  : pct(crop.left),
      bottom: 1 - pct(crop.bottom),
      right : 1 - pct(crop.right),
    };
  }
  const vClip = {
    asset : vAsset,
    start : 0,
    length,
    filter: ({
      normal   :'none',
      clarendon:'boost',
      gingham  :'muted',
      lofi     :'contrast',
      moon     :'greyscale',
      valencia :'lighten',
      xpro2    :'boost',
      willow   :'greyscale',
    }[filter.id] || 'none'),
    opacity: filter.lvl / 100,
  };

  /* ---------- text clips ---------- */
  const HALF = 225;
  const off  = p => Math.max(-1, Math.min(1, +(p / HALF).toFixed(4)));

  const htmlClips = textItems.map(t => ({
    asset:{
      type  :'html',
      width :600,
      height:200,
      html  :`<div class="box"><p class="txt">${t.html}</p></div>`,
      css   :`
        .box { display:inline-block; padding:12px 18px; background:${t.bgColor};
               border-radius:6px; }
        .txt { margin:0; font-family:${t.fontFamily}; font-size:${t.fontSize}px;
               color:${t.color}; text-align:center; }
      `,
    },
    start   :0,
    length  ,
    position:'center',
    offset  :{ x:off(t.x - HALF), y:off(t.y - HALF) },
  }));

  /* ---------- optional music track ---------- */
  const musicTrack = audio?.src ? [{
    clips:[{
      asset : { type:'audio', src:audio.src, volume: audio.volume ?? 1 },
      start : 0,
      length,
    }],
  }] : [];

  const tracks = [{ clips:[vClip] }, ...musicTrack];
  if (htmlClips.length) tracks.push({ clips:htmlClips });

  return {
    timeline:{ background:'#000000', tracks },
    output  :{ format:'mp4', size:{ width:w, height:h }, fps:30 },
  };
}

/* ================================================================
   Synchronous helper (blocks until render finishes)
================================================================ */
export async function renderWithShotstack (opts) {
  const { timeline, output } = buildTimeline(opts);
  const id = await startShotstackJob({ timeline, output });
  return pollShotstackJob(id);
}
